/**
 * t298u: activity cards read as posts about their subject, not as "X commented on Y" lines. The header is the subject
 * (issue, task, event, the thing being discussed) with its ref muted; one muted meta line says where it is and where
 * it stands (building · area · priority · status · reported by X · when); then the description and first photo;
 * reactions; and the latest one or two comments under it. At most one muted "Latest: Euan Whitfield commented
 * yesterday" line says what happened most recently, and it never quotes a comment (the comments below already do).
 */
import { DateTime } from 'luxon';
import { INCIDENT_SEVERITIES, INCIDENT_STATUSES } from '../ops/incidents';
import { formatGB } from '../gbDate';
import { feedNewestComment, type FeedClosure, type FeedComment, type FeedItem } from './feed';
import { issueChangesText, type ActivitySeg, type FeedPerson } from './issueActivity';

export interface FeedPostLatest { who: FeedPerson | string | null; what: string; at: string }
export interface FeedPost {
  /** The subject, e.g. "Toilet in staff area blocked". */
  title: string;
  /** Shown muted after the title: "#32", "WH-12". */
  ref: string | null;
  /** The meta line's parts, joined with " · " (people are pieces of their own so they open their profile). */
  meta: ActivitySeg[][];
  /** When the subject itself happened (reported, completed); the meta line ends with it. */
  when: string | null;
  description: string | null;
  /** The issue's first photo, for the thumbnail. */
  photo: { incident_id: string; attachment_id: string } | null;
  latest: FeedPostLatest | null;
  /** Every comment on the subject, when the feed knows it. */
  comments: number | null;
  /** t701u: the building it applies to, as its short code (bold, top right; the full name on hover). Its name is then left out of the meta line. */
  building?: FeedBuildingCode | null;
  /** t362u: done or closed: the card gets a thin strip across its top and the rest is quieter. */
  closure: FeedClosure | null;
}

/**
 * t362u: issue statuses. Resolved = fixed (green); Closed = closed without a fix (grey). New, Acknowledged, In progress
 * and On hold are open (no strip). The date is when it was resolved, or closed (falling back to resolved).
 */
export function issueClosure(x: { status: string; resolved_at?: string | null; closed_at?: string | null }, fallbackAt: string | null = null): FeedClosure | null {
  if (x.status === 'resolved') return { tone: 'done', label: 'Resolved', at: x.resolved_at ?? fallbackAt };
  if (x.status === 'closed') return { tone: 'closed', label: 'Closed', at: x.closed_at ?? x.resolved_at ?? fallbackAt };
  return null;
}
/**
 * t362u: task status categories. A status in the Done category (Done, Completed, …) = Completed (green); in the
 * Cancelled category (Cancelled, Not Being Implemented, Won't do, …) = Cancelled (grey). Backlog, To do and In
 * progress are open. Unknown (older servers sent only completed tasks): Completed.
 */
export function taskClosure(category: string | null | undefined, at: string | null): FeedClosure | null {
  if (category === 'cancelled') return { tone: 'closed', label: 'Cancelled', at };
  if (category === 'done' || category == null) return { tone: 'done', label: 'Completed', at };
  return null;
}
/** t362u: the card's closure (issue, completed task, or the task / issue a discussion is about). */
export function feedClosure(i: FeedItem): FeedClosure | null {
  switch (i.kind) {
    case 'issue': return issueClosure(i.issue, i.event === 'resolved' ? i.event_at ?? i.at : null);
    case 'task_completed': return taskClosure(i.task.status_category, i.event_at ?? i.at);
    case 'discussion': return i.thread.closure ?? null;
    default: return null;
  }
}
/** t362u: the strip's text: "Resolved 20 Oct", "Closed 3 Jan 2025" (the year only when it isn't this year), or just the label. */
export function feedClosureText(c: FeedClosure, now: DateTime = DateTime.local()): string {
  const d = c.at ? DateTime.fromISO(c.at).setZone(now.zone) : null;
  if (!d || !d.isValid) return c.label;
  return `${c.label} ${formatGB(d, d.year === now.year ? 'd LLL' : 'd LLL yyyy')}`;
}

const sev = (v: string) => INCIDENT_SEVERITIES.find(s => s.key === v)?.label ?? v;
const st = (v: string) => INCIDENT_STATUSES.find(s => s.key === v)?.label ?? v;
const person = (id: string | null | undefined, name: string): ActivitySeg => (id ? { id, name } : name);
const ENTITY_LABEL: Record<string, string> = { incident: 'Issue', task: 'Task', calendar_event: 'Event', kudos: 'Kudos', announcement: 'Announcement', shift_offer: 'Shift offer', message: 'Message', equipment: 'Equipment', journey: 'Journey' };
/** "Event", "Issue", "Task": what a discussion is about. */
export const feedEntityLabel = (type: string) => ENTITY_LABEL[type] ?? 'Discussion';
/** "View all 5 comments" under the latest one or two, or null when they are all shown. */
export const viewAllCommentsLabel = (total: number, shown: number) => (total > shown ? `View all ${total} comments` : null);

/** t358u: "You commented", "Euan Whitfield replied": the Latest line for a comment. */
export const commentLatest = (c: FeedComment): FeedPostLatest =>
  ({ who: c.mine ? 'You' : c.actor_id ? { id: c.actor_id, name: c.actor_name } : c.actor_name, what: c.reply_to ? 'replied' : 'commented', at: c.at });
/**
 * The newest comment on the card, if it is newer than `than`. t358u: of any kind (replies and your own included), the
 * newer of the card's latest comment and the newest one sent to you.
 */
export function newerComment(i: FeedItem, than: string | undefined): FeedPostLatest | null {
  const c = feedNewestComment(i);
  if (!c || (than && c.at <= than)) return null;
  return commentLatest(c);
}

/** The post view of an issue, completed task or discussion card; null for cards that are already posts. */
/** t701u: a building's short code for the card's top right, with its full name for the hover text. */
export interface FeedBuildingCode { code: string; name: string }
/** t701u: the corner code, or null when there is no building or it has no short code (the name then stays in the meta line). */
export const feedBuildingCode = (name: string | null | undefined, code: string | null | undefined): FeedBuildingCode | null =>
  (code && code.trim() ? { code: code.trim(), name: name || code.trim() } : null);

export function feedPost(i: FeedItem): FeedPost | null {
  switch (i.kind) {
    case 'issue': {
      const x = i.issue;
      const reported = x.created_at ?? (i.event === 'reported' ? i.at : null);
      const own = i.event_at ?? i.at;
      // The latest thing: a newer comment, else the issue's own latest change or resolution (a fresh report has none:
      // the meta line already says who reported it and when).
      const latest = newerComment(i, own)
        ?? (i.event === 'updated' ? { who: i.actor, what: issueChangesText(i.changes, i.actor?.id), at: own }
          : i.event === 'resolved' ? { who: i.actor, what: 'resolved it', at: own } : null);
      // t362u: a resolved / closed issue says so on its strip, so the meta line leaves the status out.
      const closure = feedClosure(i);
      const building = feedBuildingCode(x.location_name, x.location_code);
      return {
        title: x.title, ref: `#${x.ref}`, closure, building,
        meta: [building ? null : [x.location_name], x.location_detail ? [x.location_detail] : null, [sev(x.severity)], closure ? null : [st(x.status)], ['Reported by ', person(x.reporter_id, x.reporter_name)]]
          .filter((m): m is ActivitySeg[] => Boolean(m && m.every(s => s !== ''))),
        when: reported, description: x.description ?? null,
        photo: x.photo_id ? { incident_id: x.id, attachment_id: x.photo_id } : null,
        latest, comments: x.comment_count ?? null,
      };
    }
    case 'task_completed': {
      const t = i.task;
      const done = i.event_at ?? i.at;
      const building = feedBuildingCode(t.location_name, t.location_code);
      return {
        title: t.title, ref: t.key, closure: feedClosure(i), building,
        meta: [t.project_name ? [t.project_name] : null, t.location_name && !building ? [t.location_name] : null, ['Done', ...(t.actor_name ? [' by ', person(t.actor_id, t.actor_name)] : [])]]
          .filter((m): m is ActivitySeg[] => Boolean(m)),
        when: done, description: null, photo: null, latest: newerComment(i, done), comments: t.comment_count ?? null,
      };
    }
    case 'discussion': {
      const th = i.thread;
      // "#3 Card terminal offline" / "WH-12 Restock aisle 4": the ref goes muted after the title.
      const m = /^(#\d+|[A-Z][A-Z0-9]*-\d+)\s+(.+)$/.exec(th.title);
      const c = feedNewestComment(i);
      return {
        title: m ? m[2]! : th.title, ref: m ? m[1]! : null, closure: feedClosure(i),
        meta: [[feedEntityLabel(th.entity_type)], [`${th.count} new comment${th.count === 1 ? '' : 's'}`]],
        when: null, description: null, photo: null,
        latest: c ? commentLatest(c) : null,
        comments: null,
      };
    }
    default: return null;
  }
}

/** The latest line as plain text, without the time: "Latest: Euan Whitfield commented". */
export function feedLatestText(l: FeedPostLatest): string {
  const who = l.who ? (typeof l.who === 'string' ? l.who : l.who.name) : null;
  return who ? `Latest: ${who} ${l.what}` : `Latest: ${l.what.charAt(0).toUpperCase()}${l.what.slice(1)}`;
}
/** The meta line as plain text (tests, aria). */
export const feedPostMetaText = (p: FeedPost) => p.meta.map(m => m.map(s => (typeof s === 'string' ? s : s.name)).join('')).join(' · ');

/**
 * t362u: the strip's colours, the same on the web (Tailwind classes in FeedClosureStrip) and the app. Green for done:
 * white on emerald-700 (emerald-50 on emerald-800 in dark mode); grey for closed: slate-700 on slate-200 (slate-200
 * on slate-700 in dark mode). All pass WCAG AA (white on emerald-700 5.5:1, the rest 7:1+). Never yellow text.
 */
export const FEED_CLOSURE_COLOURS = {
  done: { light: { bg: '#047857', fg: '#ffffff' }, dark: { bg: '#065f46', fg: '#ecfdf5' } },
  closed: { light: { bg: '#e2e8f0', fg: '#334155' }, dark: { bg: '#334155', fg: '#e2e8f0' } },
} as const;
